import type { FeedAction, FeedRecordType } from "./types"

const VERBS: Record<FeedAction, string> = {
  create: "created",
  update: "edited",
  delete: "deleted",
  restore: "restored",
  import: "imported",
}

const NOUNS: Record<string, { one: string; many: string }> = {
  MineralTransaction: { one: "Transaction", many: "Transactions" },
  Well: { one: "Well", many: "Wells" },
  Note: { one: "note", many: "notes" },
  OccOperatorImport: { one: "OCC operator map", many: "OCC operator maps" },
  AssignmentDoc: { one: "lease assignment", many: "lease assignments" },
  LeaseIntakeRun: { one: "lease document import", many: "lease document imports" },
}

// "CountyOverride" -> "County Override". Fallback so an audited type we haven't
// mapped yet (or junk data) renders instead of crashing the page.
const humanize = (t: string) => t.replace(/([a-z0-9])([A-Z])/g, "$1 $2")

const nounFor = (t: string) => NOUNS[t] ?? { one: humanize(t), many: `${humanize(t)}s` }

export const verb = (a: FeedAction) => VERBS[a] ?? a
export const noun = (t: FeedRecordType, count = 1) =>
  count === 1 ? nounFor(t).one : nounFor(t).many

// "TX-2025-0042" when a label resolved; otherwise "a Transaction" / "a note".
// A Note's label is the STR it sits on, so the bare label reads like the
// section itself was created ("created 7-11N-19W") — phrase it as "a note on".
export const recordPhrase = (t: FeedRecordType, label: string | null) => {
  if (label === null) return `a ${nounFor(t).one}`
  return t === "Note" ? `a note on ${label}` : label
}

// "net_acres" -> "net acres" for the changed-fields suffix on update entries.
export const fieldLabel = (field: string) => field.replace(/_/g, " ")

export const actorName = (actor: { name: string | null; email: string } | null) =>
  actor ? actor.name || actor.email : "System"

export const initials = (actor: { name: string | null; email: string } | null) => {
  if (!actor) return "SY"
  const src = actor.name || actor.email
  const parts = src.trim().split(/\s+/)
  const first = parts[0]?.[0] ?? ""
  // Two+ words: initial of each. Single token: first two chars.
  const second = parts.length > 1 ? (parts[1]?.[0] ?? "") : (parts[0]?.[1] ?? "")
  return (first + second).toUpperCase() || "SY"
}
